import { useQuery } from '@tanstack/react-query';
import { api, type SessionInfo } from '../lib/api.js';
import { Card, Row, SectionLabel, Stat } from '../components/ui.js';

interface OverviewProps {
  session: SessionInfo;
}

interface PortfolioSummary {
  active_accounts: number;
  account_names: string[];
  generated_at: string;
  last_24h: {
    content_posts_published: number;
    gbp_posts_published: number;
    site_qa_runs: number;
    site_qa_regressions: number;
    backup_successes: number;
    backup_failures: number;
  };
}

export function OverviewPage({ session }: OverviewProps) {
  const portfolio = useQuery<PortfolioSummary>({
    queryKey: ['portfolio', 'summary'],
    queryFn: () => api.get<PortfolioSummary>('/api/dashboard/portfolio/summary'),
    staleTime: 60_000,
  });

  const firstName = session.user.display_name?.split(' ')[0] ?? null;

  return (
    <div className="space-y-14">
      {/* HERO ─────────────────────────────────────────────────────── */}
      <section>
        <p className="text-xs uppercase tracking-[0.28em] text-cream/40 mb-5">
          {session.user.role === 'operator' ? 'Operator Console' : 'Your Dashboard'}
        </p>
        <h1 className="font-serif text-5xl md:text-6xl leading-[0.95] tracking-tightest text-cream">
          {firstName ? <>Welcome back, <em className="text-cream/80">{firstName}</em>.</> : 'Welcome back.'}
        </h1>
        <p className="mt-5 text-base text-cream/55 max-w-2xl">
          {session.user.role === 'operator'
            ? 'Portfolio-wide visibility across every Bloom customer. Regressions, backups, and 24-hour activity at a glance.'
            : session.accounts.length === 1
              ? `Live overview of ${session.accounts[0]!.account_name}. Bloom is monitoring, scoring, and protecting your presence around the clock.`
              : `Live overview of your ${session.accounts.length} locations. Bloom is monitoring, scoring, and protecting your presence around the clock.`}
        </p>
      </section>

      {/* LOCATIONS (customer view only) ────────────────────────────── */}
      {session.user.role !== 'operator' && session.accounts.length > 0 && (
        <section>
          <SectionLabel>Your locations</SectionLabel>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {session.accounts.map((a) => (
              <article
                key={a.account_id}
                className="group relative rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 hover:border-cream/25 hover:bg-cream/[0.04] transition-all overflow-hidden"
              >
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cream/30 to-transparent opacity-50 group-hover:opacity-100 transition-opacity" />
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <div className="font-serif text-2xl text-cream leading-tight">{a.account_name}</div>
                    <div className="mt-2 text-[10px] uppercase tracking-[0.24em] text-cream/45">
                      {a.role}
                    </div>
                  </div>
                  <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.18em] text-emerald-400/70">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400/70 animate-pulse" />
                    Live
                  </span>
                </div>
              </article>
            ))}
          </div>
        </section>
      )}

      {/* 24H ACTIVITY ──────────────────────────────────────────────── */}
      <section>
        <SectionLabel>Last 24 hours</SectionLabel>
        {portfolio.isLoading && (
          <div className="text-sm text-cream/40">Loading…</div>
        )}
        {portfolio.isError && (
          <div className="rounded-2xl border border-cream/10 bg-cream/[0.02] p-6 text-sm text-cream/55">
            Couldn't load activity right now. Please refresh in a moment.
          </div>
        )}
        {portfolio.data && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat
              label="Active customers"
              value={portfolio.data.active_accounts}
              tone={portfolio.data.active_accounts > 0 ? 'ok' : 'neutral'}
            />
            <Stat
              label="Posts published"
              value={portfolio.data.last_24h.content_posts_published}
            />
            <Stat
              label="GBP posts"
              value={portfolio.data.last_24h.gbp_posts_published}
            />
            <Stat
              label="QA regressions"
              value={portfolio.data.last_24h.site_qa_regressions}
              tone={portfolio.data.last_24h.site_qa_regressions > 0 ? 'warn' : 'neutral'}
            />
          </div>
        )}
      </section>

      {/* BACKUPS ─────────────────────────────────────────────────── */}
      {portfolio.data && (
        <section>
          <SectionLabel>Backups (last 24h)</SectionLabel>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            <Stat
              label="Successful"
              value={portfolio.data.last_24h.backup_successes}
              tone={portfolio.data.last_24h.backup_successes > 0 ? 'ok' : 'neutral'}
            />
            <Stat
              label="Failed"
              value={portfolio.data.last_24h.backup_failures}
              tone={portfolio.data.last_24h.backup_failures > 0 ? 'warn' : 'neutral'}
            />
            <Stat
              label="Site QA runs"
              value={portfolio.data.last_24h.site_qa_runs}
            />
            <Card className="p-5 flex flex-col justify-center">
              <div className="text-[10px] uppercase tracking-[0.24em] text-cream/40">Retention</div>
              <div className="mt-1 text-sm text-cream/70">
                14 days DB · 28 days files
              </div>
            </Card>
          </div>
        </section>
      )}

      {/* SESSION META ─────────────────────────────────────────────── */}
      <section className="pt-10 border-t border-cream/[0.06]">
        <SectionLabel>Session</SectionLabel>
        <dl className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <Row label="Email">{session.user.email}</Row>
          <Row label="Role">{session.user.role}</Row>
          <Row label="Origin">{session.session.origin}</Row>
          <Row label="Expires">
            {new Date(session.session.expires_at).toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </Row>
        </dl>
      </section>
    </div>
  );
}
